import { Component, computed, input, InputSignal, Signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { KYC_KYB_URLS } from '@app/models/kyc-kyb/types';
import { PersonaScope } from '@app/modules/payroll/components/kyc-kyb/types';
import { AppDatePipe } from '@app/pipes';
import { TranslateModule } from '@ngx-translate/core';
import { BaseInformationBar } from '../base-information-type.component';

/**
 * Banner shown to users that must complete the Know-Your-Client or Know-Your-Business flow through Persona
 */
@Component({
    selector: 'special-info-kyc-kyb-with-wave',
    standalone: true,
    imports: [TranslateModule, MatIconModule, RouterLink, AppDatePipe],
    templateUrl: './kyc-kyb-with-wave.template.html',
    styleUrls: ['./kyc-kyb-with-wave.style.scss'],
})
export class SpecialInfoKYCKYBWithWave extends BaseInformationBar {
    scope: InputSignal<PersonaScope> = input.required();

    shouldDisplay: Signal<boolean> = computed(() => {
        const {
            hasPayroll,
            isAdmin,
            KYCKYB,
            currentURL,
            hasKYCExpired,
            isEmployeeOnboarding,
            hasKYBExpired,
            kycKybWave,
        } = this.displayConditions() || {};

        // Don't show if employee is onboarding
        if (isEmployeeOnboarding) {
            return false;
        }

        // Don't show if re-verification is required (re-verification banner takes priority)
        if (hasKYBExpired) {
            return false;
        }

        // Do not show if KYC/KYB is disabled, or this company is not part of any waves
        if (!KYCKYB || !kycKybWave) {
            return false;
        }

        // Do not show if the current wave has not started
        if (kycKybWave.startDate && new Date(kycKybWave.startDate) > new Date()) {
            return false;
        }

        const { hasVerifiedPersonaIdentity, hasVerifiedPersonaBusiness } = KYCKYB;

        // If we're already on the KYC or KYB page we don't need to direct to itself (KYC still links to KYB and vice-versa)
        if (KYC_KYB_URLS[this.scope()] === currentURL) {
            return false;
        }

        // KYC
        if (this.scope() === 'KYC') {
            if ((hasPayroll || hasKYCExpired) && !hasVerifiedPersonaIdentity) {
                return true;
            }

            return false;
        }

        // KYB
        if (hasPayroll && !!isAdmin && !hasVerifiedPersonaBusiness) {
            return true;
        }
        return false;
    });

    get KYBKYCExpirationDate(): string {
        return this.displayConditions()?.kycKybWave?.deadline?.toString() ?? '';
    }

    get linkToPreRedirect(): string {
        return KYC_KYB_URLS[this.scope()];
    }

    get hasKYCExpired(): boolean {
        return this.displayConditions()?.hasKYCExpired ?? false;
    }

    get verificationDays(): number {
        const displayConditions = this.displayConditions();
        const verificationDays = displayConditions?.kycKybWave?.verificationDays;

        if (verificationDays) {
            return verificationDays;
        }

        const startDate = displayConditions?.kycKybWave?.startDate;
        const deadline = displayConditions?.kycKybWave?.deadline;

        if (startDate && deadline) {
            const duration = new Date(deadline).getTime() - new Date(startDate).getTime();
            return Math.ceil(duration / (1000 * 3600 * 24));
        }
        return 0;
    }
}
